import {
  MessageSquare, Image, Mic, RefreshCw, Sparkles, User, List, Target, Info, AlertTriangle, Lock, Scale, PenLine, BookOpen,
  Hash, Layers, Thermometer, Cpu, Copy, Braces, Link2, Check, Zap, Send, Wrench, Shield, Briefcase, Box, Users, Heart,
  DollarSign, Star, Play, Trophy, Shirt, Home, Gem, Lightbulb, CheckCircle2, FlaskConical, X,
} from "lucide-react";
import { ProfessorSays } from "@/components/campus/Professor";

const ICONS = {
  message: MessageSquare, image: Image, mic: Mic, refresh: RefreshCw, sparkles: Sparkles, user: User, list: List, target: Target,
  info: Info, alert: AlertTriangle, lock: Lock, scale: Scale, pen: PenLine, book: BookOpen, hash: Hash, layers: Layers,
  thermo: Thermometer, cpu: Cpu, copy: Copy, braces: Braces, link: Link2, check: Check, zap: Zap, send: Send, wrench: Wrench,
  shield: Shield, briefcase: Briefcase, box: Box, users: Users, heart: Heart, dollar: DollarSign, star: Star, play: Play,
  trophy: Trophy, shirt: Shirt, home: Home, gem: Gem,
};

const Rich = ({ text }) => (
  <>{String(text).split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") ? <strong key={i} className="text-white">{p.slice(2, -2)}</strong> : p))}</>
);

const Heading = ({ children }) => children ? <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">{children}</h3> : null;

const Cards = ({ b }) => (
  <div>
    <Heading>{b.title}</Heading>
    <div className="grid sm:grid-cols-2 gap-3">
      {b.items.map((c) => {
        const I = ICONS[c.icon] || Sparkles;
        return (
          <div key={c.title} className="rounded-xl bg-[#0B0E14] border border-white/10 p-4 flex gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center shrink-0"><I className="h-5 w-5" /></div>
            <div><div className="font-semibold text-white text-sm">{c.title}</div><p className="text-sm text-slate-400 mt-0.5 leading-relaxed">{c.text}</p></div>
          </div>
        );
      })}
    </div>
  </div>
);

const Steps = ({ b }) => (
  <div>
    <Heading>{b.title}</Heading>
    <ol className="space-y-2.5">
      {b.items.map((s, i) => (
        <li key={i} className="flex gap-3 items-start">
          <span className="h-7 w-7 rounded-full bg-amber-500 text-slate-950 text-xs font-extrabold flex items-center justify-center shrink-0">{i + 1}</span>
          <span className="text-slate-200 text-[15px] leading-relaxed pt-0.5"><Rich text={s} /></span>
        </li>
      ))}
    </ol>
  </div>
);

const Table = ({ b }) => (
  <div>
    <Heading>{b.title}</Heading>
    <div className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-white/5"><tr>{b.cols.map((c, i) => <th key={i} className="text-left px-4 py-2.5 text-amber-300 font-semibold whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>{b.rows.map((r, i) => <tr key={i} className="border-t border-white/5">{r.map((cell, j) => <td key={j} className={`px-4 py-2.5 align-top ${j === 0 ? "text-white font-medium" : "text-slate-300"}`}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  </div>
);

const Compare = ({ b }) => (
  <div>
    <Heading>{b.title}</Heading>
    <div className="grid sm:grid-cols-2 gap-3">
      <div className="rounded-xl border border-red-500/30 bg-red-500/[0.06] p-4">
        <div className="flex items-center gap-1.5 text-red-300 text-xs font-bold uppercase mb-1.5"><X className="h-3.5 w-3.5" /> {b.bad.label}</div>
        <p className="text-sm text-slate-300 font-mono leading-relaxed">{b.bad.text}</p>
      </div>
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4">
        <div className="flex items-center gap-1.5 text-emerald-300 text-xs font-bold uppercase mb-1.5"><Check className="h-3.5 w-3.5" /> {b.good.label}</div>
        <p className="text-sm text-slate-200 font-mono leading-relaxed">{b.good.text}</p>
      </div>
    </div>
  </div>
);

const Callout = ({ b }) => {
  const warn = b.t === "warn";
  const I = warn ? AlertTriangle : Lightbulb;
  return (
    <div className={`rounded-xl p-4 flex gap-3 border ${warn ? "bg-red-500/[0.06] border-red-500/30" : "bg-sky-500/[0.06] border-sky-500/30"}`}>
      <I className={`h-5 w-5 shrink-0 mt-0.5 ${warn ? "text-red-300" : "text-sky-300"}`} />
      <div><div className={`text-xs font-bold uppercase mb-0.5 ${warn ? "text-red-300" : "text-sky-300"}`}>{warn ? "Watch out" : "Pro tip"}</div><p className="text-sm text-slate-200 leading-relaxed"><Rich text={b.text} /></p></div>
    </div>
  );
};

const ListBox = ({ b, tone }) => {
  const t = tone === "try"
    ? { cls: "bg-violet-500/[0.07] border-violet-500/30", head: "text-violet-300", Icon: FlaskConical, label: b.title || "Try it now" }
    : { cls: "bg-emerald-500/[0.06] border-emerald-500/30", head: "text-emerald-300", Icon: CheckCircle2, label: "Key takeaways" };
  return (
    <div className={`rounded-xl border p-4 ${t.cls}`}>
      <div className={`flex items-center gap-1.5 text-xs font-bold uppercase mb-2 ${t.head}`}><t.Icon className="h-4 w-4" /> {t.label}</div>
      <ul className="space-y-1.5">{b.items.map((i, k) => <li key={k} className="text-sm text-slate-200 flex gap-2"><span className={t.head}>•</span><Rich text={i} /></li>)}</ul>
    </div>
  );
};

const Block = ({ b }) => {
  switch (b.t) {
    case "say": return <ProfessorSays text={b.text} />;
    case "text": return <p className="text-slate-300 text-[15px] leading-relaxed whitespace-pre-line"><Rich text={b.text} /></p>;
    case "cards": return <Cards b={b} />;
    case "steps": return <Steps b={b} />;
    case "table": return <Table b={b} />;
    case "compare": return <Compare b={b} />;
    case "tip": case "warn": return <Callout b={b} />;
    case "try": return <ListBox b={b} tone="try" />;
    case "takeaways": return <ListBox b={b} tone="takeaways" />;
    default: return null;
  }
};

export const LessonBlocks = ({ blocks }) => (
  <div className="space-y-6 mt-5" data-testid="lesson-blocks">
    {blocks.map((b, i) => <Block key={i} b={b} />)}
  </div>
);
